/** @jsxImportSource @emotion/react */
"use client";
import type { Interpolation } from "@emotion/react";
import type { JSX, ReactNode } from "react";
import { forwardRef, Fragment } from "react";

import type { ThemeType } from "../../../../../global/emotion";
import { spacing } from "../../../../../global/emotion";
import type { HallowElement } from "../../../../types/hallowElement.types";
import type { HallowElementProps } from "../../../../types/hallowElementProps.types";
import type { KeyedElement } from "../../../../types/keyedElement.types";
import type { WithAsChild } from "../../../../types/withAsChild.types";
import { determineChildrenInject } from "../../../../utils/determineChildrenInject";
import { determineElementFromAsChild } from "../../../../utils/determineElementFromAsChild";
import { PlaceholderIcon } from "../../icons/Icon/GeneralIcons";
import { type IconElement, InlineIcon } from "../../icons/Icon/Icon";
import { MoreIcon } from "../../icons/Icon/NavigationIcons";
import { ModalDropdownMenu } from "../../modals/ModalDropdownMenu/ModalDropdownMenu.emotion";
import {
  baseStyle as listBaseStyle,
  contentStyle,
  imageStyle,
  metaDataStyle,
} from "../listStyles.emotion";
import { Text } from "../Text/Text.emotion";

const hallowElement: HallowElement = "div";

const baseStyle = [listBaseStyle, { WebkitTapHighlightColor: "transparent" }];

const disabledStyle: Interpolation = {
  opacity: 0.3,
  pointerEvents: "none",
};
const numberStyle: Interpolation<ThemeType> = (theme) => ({
  color: theme.semanticColors.neutralsMedium,
});
const rightActionsContainerStyle: Interpolation = {
  alignItems: "center",
  display: "flex",
  gap: spacing.m,
};
const metaDataWidthAutoStyle: Interpolation = {
  width: "auto",
};
const metaDataWidthFitStyle: Interpolation = {
  minWidth: "fit-content",
};

export type ListCollectionDetailProps = WithAsChild<
  HallowElementProps<typeof hallowElement>
> & {
  description?: string;
  disabled?: boolean;
  imageSrc?: string;
  metaData?: string[];
  metaDataIcon?: IconElement;
  metaDataStartIcon?: IconElement | null;
  moreMenuContent?: ReactNode;
  number?: JSX.Element | number | string;
  rightActions?: KeyedElement | KeyedElement[];
  title: string;
};

export const ListCollectionDetail = forwardRef<any, ListCollectionDetailProps>(
  (
    {
      asChild = false,
      children,
      description,
      disabled = false,
      imageSrc,
      metaData,
      metaDataIcon = <PlaceholderIcon />,
      metaDataStartIcon = null,
      moreMenuContent,
      number = "00",
      rightActions,
      title,
      ...props
    },
    ref,
  ) => {
    const DeterminedElement = determineElementFromAsChild({
      asChild,
      hallowElement,
    });

    const DeterminedChildren = determineChildrenInject({
      afterChildrenInject: [
        number ? (
          <Text key="number" size="s" css={numberStyle} type="title">
            {number}
          </Text>
        ) : null,
        imageSrc ? (
          <img alt={description} key="img" src={imageSrc} css={imageStyle} />
        ) : null,
        <span key="content" css={contentStyle}>
          <Text key={"title"} overflow="ellipsis" size="m" type="title">
            {title}
          </Text>
          {description && (
            <Text
              key={"description"}
              overflow="ellipsis"
              size="xl"
              type="detail"
            >
              {description}
            </Text>
          )}
          {metaData?.length > 0 && (
            <div css={metaDataStyle}>
              {metaDataStartIcon ? (
                <InlineIcon
                  key={"metadataStartIcon"}
                  icon={metaDataStartIcon}
                />
              ) : null}
              {metaData.length > 1 && (
                <Text
                  overflow="ellipsis"
                  size="l"
                  css={metaDataWidthAutoStyle}
                  type="detail"
                >
                  {metaData[0]}
                </Text>
              )}
              {metaData.slice(1).map((metaDataItem, index) =>
                metaDataItem ? (
                  <Fragment key={`${metaDataItem}${index}`}>
                    <InlineIcon icon={metaDataIcon} />
                    <Text size="l" css={metaDataWidthFitStyle} type="detail">
                      {metaDataItem}
                    </Text>
                  </Fragment>
                ) : null,
              )}
            </div>
          )}
        </span>,
        moreMenuContent ? (
          <ModalDropdownMenu trigger={<MoreIcon key="icon-more" />}>
            {moreMenuContent}
          </ModalDropdownMenu>
        ) : null,
        rightActions ? (
          <div key={"rightActions"} css={rightActionsContainerStyle}>
            {rightActions}
          </div>
        ) : null,
      ],
      children: children as JSX.Element,
    });

    return (
      <DeterminedElement
        ref={ref}
        css={[baseStyle, disabled && disabledStyle]}
        {...props}
      >
        {DeterminedChildren}
      </DeterminedElement>
    );
  },
);

ListCollectionDetail.displayName = "ListCollectionDetail";
